// @spec authentication-google-oauth

/**
 * Cookies, max-ages, and string-literal types used by the "Link another
 * Google account" flow. Centralized here (rather than scattered through the
 * generic `constants.ts` grab-bag) so the link-flow surface stays
 * navigable as it grows.
 */

/** Cookie set before "Add Account" redirect so the signIn callback allows
 *  intentional linking. Non-httpOnly because the client-side `startLinkFlow`
 *  helper sets it via `document.cookie` before the browser navigates to
 *  Google; the server reads + clears it on return. */
export const LINK_INTENT_COOKIE = 'fpo3-link-intent';

/**
 * Cookie set alongside {@link LINK_INTENT_COOKIE} in `startLinkFlow` so the
 * NextAuth signIn callback can recover the *initiator's* user id without
 * re-entering `auth()`. Carries the user id of the currently signed-in user
 * who clicked "Add another account" — distinct from the user being linked
 * (that's in `account.providerAccountId`). Non-httpOnly because it's set
 * client-side; the user id is not a secret (it ships in the session JWT).
 *
 * Lifecycle mirrors {@link LINK_INTENT_COOKIE}: set on link-flow start,
 * cleared by the signIn callback in the same `finally` block as the intent
 * cookie so the two cookies are always consumed together.
 */
export const LINK_INITIATOR_USER_ID = 'fpo3-link-initiator';

/**
 * Non-httpOnly cookie set by the server after a "Link account" OAuth round-trip
 * to communicate the outcome back to the settings page banner. Values are the
 * `LinkStatusValue` literals below. Short-lived (5 min) and cleared by the
 * settings page on read. Non-httpOnly because the client reads + clears it;
 * no sensitive data inside.
 */
export const LINK_STATUS_COOKIE = 'fpo3-link-status';

/** Max-age (seconds) for {@link LINK_STATUS_COOKIE}; long enough to survive
 *  the OAuth round-trip + post-redirect render, short enough that a stale
 *  value can't outlast a new attempt. */
export const LINK_STATUS_COOKIE_MAX_AGE_SEC: number = 5 * 60;

export const LINK_STATUS_VALUES = [
  'newly-linked',
  'already-linked',
  'insufficient-scope',
  'wrong-user',
] as const;
export type LinkStatusValue = (typeof LINK_STATUS_VALUES)[number];
